import { useState, useCallback, useMemo } from 'react';
import type { CreditCardInfo, CreditCardFormErrors } from '../types';

/**
 * useCreditCardInput フックの戻り値
 */
interface UseCreditCardInputReturn {
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  securityCode: string;
  cardName: string;
  errors: CreditCardFormErrors;
  isValid: boolean;
  setCardNumber: (value: string) => void;
  setExpiryMonth: (value: string) => void;
  setExpiryYear: (value: string) => void;
  setSecurityCode: (value: string) => void;
  setCardName: (value: string) => void;
  validate: () => boolean;
  getCardInfo: () => CreditCardInfo;
  expiryMonths: string[];
  expiryYears: string[];
}

/**
 * カード番号のフォーマット（4桁区切り）
 */
const formatCardNumber = (value: string): string => {
  const digitsOnly = value.replace(/\D/g, '');
  const limited = digitsOnly.slice(0, 16);
  const groups = limited.match(/.{1,4}/g);
  return groups ? groups.join(' ') : '';
};

/**
 * カード名義のバリデーション（半角英字とスペースのみ許可）
 * 入力時はフィルタリングせず、submit時にバリデーションで検証する
 */
const isValidCardName = (value: string): boolean => {
  if (!value) return true;
  return /^[A-Za-z ]+$/.test(value);
};

/**
 * クレジットカード入力を管理するフック
 */
export const useCreditCardInput = (): UseCreditCardInputReturn => {
  const [cardNumber, setCardNumberState] = useState('');
  const [expiryMonth, setExpiryMonth] = useState('');
  const [expiryYear, setExpiryYear] = useState('');
  const [securityCode, setSecurityCodeState] = useState('');
  const [cardName, setCardNameState] = useState('');
  const [errors, setErrors] = useState<CreditCardFormErrors>({});
  const [isValid, setIsValid] = useState(false);

  const setCardNumber = useCallback((value: string) => {
    const formatted = formatCardNumber(value);
    if (formatted.length <= 19) {
      setCardNumberState(formatted);
    }
  }, []);

  const setSecurityCode = useCallback((value: string) => {
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length <= 4) {
      setSecurityCodeState(digitsOnly);
    }
  }, []);

  const setCardName = useCallback((value: string) => {
    setCardNameState(value);
  }, []);

  const validate = useCallback((): boolean => {
    const newErrors: CreditCardFormErrors = {};

    if (cardNumber.replace(/\s/g, '').length !== 16) {
      newErrors.cardNumber = 'カード番号を正しく入力してください';
    }
    if (!expiryMonth) {
      newErrors.expiryMonth = '有効期限（月）を選択してください';
    }
    if (!expiryYear) {
      newErrors.expiryYear = '有効期限（年）を選択してください';
    }
    if (!/^\d{3,4}$/.test(securityCode)) {
      newErrors.securityCode = '3桁または4桁で入力してください';
    }
    if (!cardName.trim()) {
      newErrors.cardName = 'カード名義を入力してください';
    } else if (!isValidCardName(cardName)) {
      newErrors.cardName = 'カード名義は半角英字で入力してください';
    }

    setErrors(newErrors);
    const valid = Object.keys(newErrors).length === 0;
    setIsValid(valid);
    return valid;
  }, [cardNumber, expiryMonth, expiryYear, securityCode, cardName]);

  const getCardInfo = useCallback((): CreditCardInfo => {
    return {
      cardNumber: cardNumber.replace(/\s/g, ''),
      expiryMonth,
      expiryYear,
      securityCode,
      cardName: cardName.toUpperCase(),
    };
  }, [cardNumber, expiryMonth, expiryYear, securityCode, cardName]);

  const expiryMonths = useMemo(
    () => Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0')),
    []
  );

  const expiryYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, i) => (currentYear + i).toString());
  }, []);

  return {
    cardNumber,
    expiryMonth,
    expiryYear,
    securityCode,
    cardName,
    errors,
    isValid,
    setCardNumber,
    setExpiryMonth,
    setExpiryYear,
    setSecurityCode,
    setCardName,
    validate,
    getCardInfo,
    expiryMonths,
    expiryYears,
  };
};
